import { Button, Form, Popconfirm, Typography } from "antd";
import { FontAwesomeIcon, Icon } from "assets/icons";
import CustomTable from "components/CustomTable";
import { REGEX } from "config/constants";
import { useWarehouse } from "hooks/warehouse";
import { MAX_VARIANT_IMAGES, PRODUCT_INPUT_TYPE } from "pages/App/Product/ProductGroup/conts";
import { v4 as uuidv4 } from "uuid";

import { PlusCircleOutlined } from "@ant-design/icons";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import EditableCell from "../EditableCell";
import ImageStack from "../ImageStack";
import styles from "./index.module.scss";

const VariantTable = ({
  form,
  attributes,
  products,
  dataSource,
  setDataSource,
  disabledInputPrice,
  isActionCreate,
}) => {
  const { warehouses } = useWarehouse();
  const [editingKey, setEditingKey] = useState("");
  const [newlyAddedKeys, setNewlyAddedKeys] = useState(() => new Set());
  const { t } = useTranslation();
  const fieldOpsRef = useRef({});
  const editingSnapshotRef = useRef(null);

  //get option attribute for variant table when use select isVariant true
  const attributesOption = attributes
    ?.filter((attribute) => attribute?.isVariant)
    ?.map((attribute) =>
      attribute?.attributeValues?.filter((attributeValue) => attribute?.attributeValueIDs?.includes(attributeValue?.id))
    )
    ?.filter((attribute) => attribute && attribute.length);

  const addColumnAttributes =
    attributes
      ?.filter((item) => item?.isVariant)
      ?.map((item, indexCol) => {
        const options =
          attributesOption?.at(indexCol)?.map((item) => ({
            label: item?.value,
            value: item?.id,
          })) || [];

        return {
          title: <span className={styles.title}>{item?.name}</span>,
          align: "center",
          editable: true,
          width: "100",
          inputType: PRODUCT_INPUT_TYPE.SELECT,
          nameInput: ["attributeValues", indexCol],
          options: options,
          attributeLabel: item?.name,
          render: (text, record, indexRow) => {
            const attributes = form.getFieldsValue()?.products?.at(indexRow)?.attributeValues;
            const colOpt = options?.find((item) => attributes?.some((att) => att === item?.value));
            return colOpt?.label;
          },
        };
      }) || [];

  const renderColumnsWarehouse = warehouses?.map((warehouse) => {
    return {
      title: (
        <span className={`${styles.title} ${styles.filter}`}>
          {warehouse?.name}
          <Button type="text" size="small" icon={<FontAwesomeIcon icon={Icon.faFilter} />} />
        </span>
      ),
      width: 335,
      align: "center",
      editable: true,
    };
  });

  const columns = [
    //sticky
    {
      title: (
        <p className={styles.title} style={{ textAlign: "center", margin: 0 }}>
          {t("product.productForm.image")}
        </p>
      ),
      width: 200,
      fixed: "left",
      editable: true,
      inputType: PRODUCT_INPUT_TYPE.UPLOAD_IMAGE,
      nameInput: ["imageURLs"],

      render: (text, record) => {
        const images = record?.images?.length ? record.images : [record?.image].filter(Boolean);
        return <ImageStack images={images} />;
      },
    },
    {
      title: <span className={styles.title}>{t("product.productForm.productCode")}</span>,
      width: 120,
      align: "center",
      fixed: "left",
      editable: true,
      inputType: PRODUCT_INPUT_TYPE.INPUT_PRODUCT_CODE,
      nameInput: ["code"],
      render: (text, record, index) => record?.productCode,
    },
    {
      title: <span className={styles.title}>{t("product.productForm.productName")}</span>,
      width: 200,
      align: "center",
      editable: true,
      inputType: PRODUCT_INPUT_TYPE.INPUT_SHORT_CODE,
      nameInput: ["name"],
      render: (text, record, index) => record?.name,
    },
    ...addColumnAttributes,
    {
      title: "LOẠI % VAT",
      dataIndex: "vatPercent",
      align: "center",
      render: (_, record) => {
        return <div>{record.vatPercent >= 0 ? `${record.vatPercent}%` : "KCT"}</div>;
      },
      width: 200,
      editable: true,
      inputType: PRODUCT_INPUT_TYPE.VAT_PERCENT_TYPE,
    },
    {
      title: (
        <div className={`${styles.title} ${styles.filter} ${styles["label-price"]}`}>
          <span>GIÁ CÓ VAT</span>
          <Button type="text" size="small" icon={<FontAwesomeIcon icon={Icon.faFilter} />} />
        </div>
      ),
      width: 200,
      align: "center",
      editable: true,
      inputType: PRODUCT_INPUT_TYPE.INPUT_CURRENCY,
      nameInput: ["productPrices", "price"],
      render: (text, record, index) =>
        record?.price !== undefined && record?.price !== null
          ? String(record.price).replace(REGEX.CURRENCY, ".")
          : record?.price,
    },
    {
      title: <span className={styles.title}>{t("product.productForm.action")}</span>,
      align: "center",
      width: 120,
      fixed: "right",
      render: (text, record, index) => {
        const editable = isEditing(record);
        const canDelete = isActionCreate || newlyAddedKeys.has(record?.key);
        return editable ? (
          <span>
            <Typography.Link
              style={{
                marginRight: 8,
              }}
              onClick={() => handleSave(record?.key)}
            >
              {t("common.save")}
            </Typography.Link>
            <Popconfirm
              title={t("common.isCancel")}
              cancelText={t("common.cancel")}
              okText={t("common.confirm")}
              onConfirm={() => handleCancel(record?.key, canDelete)}
            >
              <a>{t("common.cancel")}</a>
            </Popconfirm>
          </span>
        ) : (
          <span>
            {disabledInputPrice && (
              <Typography.Link
                style={{
                  marginRight: 8,
                }}
                onClick={() => handleEdit(record)}
              >
                {t("common.edit")}
              </Typography.Link>
            )}
            {canDelete && (
              <Popconfirm
                title={t("product.productForm.isDeleteVariable")}
                cancelText={t("common.cancel")}
                okText={t("common.confirm")}
                onConfirm={() => handleDelete(record?.key)}
              >
                <a>{t("common.delete")}</a>
              </Popconfirm>
            )}
          </span>
        );
      },
    },
  ];

  const isEditing = (record) => record.key === editingKey;

  const handleEdit = (record) => {
    const index = dataSource.findIndex((item) => item.key === record.key);
    editingSnapshotRef.current = index > -1 ? { index, value: form.getFieldValue(["products", index]) } : null;
    setEditingKey(record.key);
  };

  const handleDelete = (key) => {
    const currentProducts = form.getFieldValue("products") || [];
    const fieldIndex = currentProducts.findIndex((item) => item?.key === key);

    if (fieldIndex > -1) {
      fieldOpsRef.current.remove?.(fieldIndex);
    }
    setDataSource(dataSource.filter((item) => item.key !== key));
    setNewlyAddedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const handleCancel = (key, canDelete) => {
    if (canDelete) {
      handleDelete(key);
    } else if (editingSnapshotRef.current) {
      const { index, value } = editingSnapshotRef.current;
      const currentProducts = form.getFieldValue("products") || [];
      const restoredProducts = [...currentProducts];
      restoredProducts[index] = value;
      form.setFieldsValue({ products: restoredProducts });
    }
    editingSnapshotRef.current = null;
    setEditingKey("");
  };

  const handleSave = async (key) => {
    const newDataSource = [...dataSource];
    const index = newDataSource.findIndex((item) => item.key === key);
    if (index > -1) {
      const editableFieldNames = columns
        .filter((col) => col.editable && col.nameInput)
        .map((col) => ["products", index, ...col.nameInput]);
      if (editableFieldNames.length) {
        try {
          await form.validateFields(editableFieldNames);
        } catch {
          return;
        }
      }

      const item = newDataSource[index];
      const imageFiles = products[index]?.imageURLs || [];
      const imageFile = imageFiles[0];
      const formVatPercent = form.getFieldValue(["products", index, "vatPercent"]);
      const itemEdit = {
        isActive: true,
        image: imageFile?.thumbUrl || imageFile?.url || "",
        images: imageFiles.map((file) => file?.thumbUrl || file?.url).filter(Boolean),
        productCode: products[index]?.code,
        name: products[index]?.name,
        price: products[index]?.productPrices?.price,
        priceVat: products[index]?.productPrices?.priceVat,
        vat: products[index]?.vat || true,
        vatPercent: formVatPercent ?? 0,
      };
      newDataSource.splice(index, 1, { ...item, ...itemEdit });
      setDataSource(newDataSource);
      setEditingKey("");
    }
  };

  const isDisableUpload = (index) => {
    const imageCount =
      form?.getFieldsValue()?.products?.at(index)?.imageURLs?.length || products?.at(index)?.imageURLs?.length || 0;
    return imageCount >= MAX_VARIANT_IMAGES;
  };
  const mergedColumns = columns.map((col, index) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record, rowIndex) => ({
        record,
        inputType: col.inputType,
        nameInput: col.nameInput,
        dataIndex: col.dataIndex,
        editing: isEditing(record),
        options: col?.options,
        rowIndex: rowIndex,
        initialValue: col.initialValue,
        isDisableUpload: isDisableUpload(rowIndex),
        attributeLabel: col?.attributeLabel,
        form,
      }),
    };
  });

  //auto increment the numeric suffix of the highest existing variant code, fallback to the group code
  const getNextProductCode = (existingProducts, fallbackCode) => {
    const codes = existingProducts?.map((product) => product?.code).filter(Boolean) || [];

    const parsedCodes = codes
      .map((code) => {
        const match = code.match(/^(.*?)(\d+)$/);
        if (!match) return null;
        return { prefix: match[1], digits: match[2], value: parseInt(match[2], 10) };
      })
      .filter(Boolean);

    if (!parsedCodes.length) return fallbackCode;

    const maxCode = parsedCodes.reduce((max, current) => (current.value > max.value ? current : max));
    const nextValue = maxCode.value + 1;
    const nextDigits = String(nextValue).padStart(maxCode.digits.length, "0");

    return `${maxCode.prefix}${nextDigits}`;
  };

  const handleAdd = () => {
    const values = form.getFieldsValue();
    const nextCode = getNextProductCode(values?.products, values?.code);

    const newData = {
      key: uuidv4(),
      isActive: true,
      image: "",
      productCode: nextCode,
      price: 0,
      priceVat: 0,
      vat: true,
      vatPercent: 0,
    };

    const newProducts = {
      key: newData?.key,
      name: values?.name,
      code: nextCode,
      inStock: 0,
      vat: true,
      vatPercent: 0,
      productPrices: { price: 0, priceVat: 0 },
      attributeValues: [],
      imageURLs: [],
      isActive: true,
    };

    fieldOpsRef.current.add?.(newProducts);
    setDataSource([...dataSource, newData]);
    setNewlyAddedKeys((prev) => new Set(prev).add(newData.key));
    handleEdit(newData);
  };

  return (
    <>
      <Form.List name="products">
        {(fields, { add, remove }) => {
          fieldOpsRef.current = { add, remove };
          return (
            <CustomTable
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              columns={mergedColumns}
              scroll={{ x: 1500, y: null }}
              dataSource={dataSource}
            />
          );
        }}
      </Form.List>
      {disabledInputPrice && (
        <div className={styles["add-variant"]} onClick={handleAdd}>
          <PlusCircleOutlined className={styles.icon} />
          <span className={styles.text}>{t("product.productForm.addVariant")}</span>
        </div>
      )}
    </>
  );
};

export default VariantTable;
